import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ContractsService } from '../../../services/contracts.service';
import { B2bContract, ContractFreezeResponse } from '../../../models/Contract';
import { statusAllowsContractFreeze, statusAllowsContractUnfreeze } from '../../../utils/contract-freeze.utils';
import { ErrorService } from '../../../shared/services/error.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { B2B_EXPORT_PAGE_SIZE, exportB2bTableToExcel } from '../../../utils/b2b-table-export.util';
import { PaymentService } from '../../../services/payment.service';
import {
  ContractCancelReason,
  CONTRACT_CANCEL_REASON_SELECT_OPTIONS,
  contractCancelMotivoFromCode
} from '../../../constants/contract-cancel-reasons';

@Component({
  selector: 'app-b2b-contracts',
  templateUrl: './b2b-contracts.component.html',
  styleUrls: ['./b2b-contracts.component.css']
})
export class B2bContractsComponent implements OnInit {
  contracts: B2bContract[] = [];
  isLoading = false;
  searchTerm = '';
  selectedStatus = '';

  // Paginação (backend)
  pageNumber = 1;
  pageSize = 10;
  totalItems = 0;
  showCancelModal = false;
  contractToCancel: B2bContract | null = null;
  cancelReasonCode: ContractCancelReason | '' = '';
  showFreezeModal = false;
  contractFreezeId: number | null = null;
  contractFreezeSubtitle = '';

  readonly contractCancelReasonOptions = CONTRACT_CANCEL_REASON_SELECT_OPTIONS;

  // Configuração da tabela
  columns = [
    { titulo: 'Número', label: 'contractNumber' },
    { titulo: 'Nome do Contrato', label: 'contractName' },
    { titulo: 'Empresa', label: 'companyName' },
    { titulo: 'CNPJ', label: 'cnpj', formatter: (value: any) => this.formatCnpj(value) },
    { titulo: 'Período', label: 'period' },
    { titulo: 'Status', label: 'statusLabel' }
  ];

  /** Botão visível na linha; o restante fica no menu (três pontinhos). */
  visibleActions: TableAction[] = [
    { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', visible: true, class: 'btn-view' }
  ];

  /** Menu ⋮ vem de `_actions` por linha (Congelar ou Descongelar, não os dois). */
  actions: TableAction[] = [];

  // Opções para filtros
  statusOptions = [
    { value: '', label: 'Todos os status' },
    { value: 'Draft', label: 'Rascunho' },
    { value: 'Pending', label: 'Pendente' },
    { value: 'Signed', label: 'Assinado' },
    { value: 'Active', label: 'Ativo' },
    { value: 'Expired', label: 'Expirado' },
    { value: 'Cancelled', label: 'Cancelado' }
  ];

  constructor(
    private router: Router,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private paymentService: PaymentService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadContracts();
  }

  /** Congelar e Descongelar são mutuamente exclusivos no menu ⋮. */
  private buildB2bRowDropdownActions(status: string | undefined): TableAction[] {
    const list: TableAction[] = [
      { label: 'Editar', icon: 'fas fa-edit', action: 'edit', class: 'btn-edit' },
      { label: 'Condutores', icon: 'fas fa-users', action: 'drivers', class: 'btn-drivers' },
      { label: 'Novo Condutor', icon: 'fas fa-user-plus', action: 'newDriver', class: 'btn-primary' },
      { label: 'Download', icon: 'fas fa-download', action: 'download', class: 'btn-download' }
    ];
    if (statusAllowsContractFreeze(status)) {
      list.push({ label: 'Congelar', icon: 'fas fa-snowflake', action: 'freeze', class: '' });
    } else if (statusAllowsContractUnfreeze(status)) {
      list.push({ label: 'Descongelar', icon: 'fas fa-sun', action: 'unfreeze', class: '' });
    }
    list.push({ label: 'Cancelar Contrato', icon: 'fas fa-ban', action: 'cancel', class: 'btn-delete' });
    return list;
  }

  loadContracts(): void {
    this.isLoading = true;

    this.contractsService.listB2bContracts(this.pageNumber, this.pageSize).subscribe({
      next: (response) => {
        // Transformar dados para a tabela
        this.contracts = response.data.map(contract => ({
          ...contract,
          period: `${this.formatDate(contract.startDate)} até ${this.formatDate(contract.endDate)}`,
          statusLabel: this.getStatusLabel(contract.status),
          _actions: this.buildB2bRowDropdownActions(contract.status)
        }));

        this.totalItems = response.pagination.totalItems;
        this.isLoading = false;
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isLoading = false;
      }
    });
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
    this.pageNumber = 1;
    // TODO: Implementar busca quando endpoint suportar filtro de busca
    this.loadContracts();
  }

  onStatusChange(): void {
    this.pageNumber = 1;
    // TODO: Implementar filtro quando endpoint suportar filtro por status
    this.loadContracts();
  }

  onPageChange(page: number): void {
    this.pageNumber = page;
    this.loadContracts();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageNumber = 1;
    this.loadContracts();
  }

  newContract(): void {
    this.router.navigate(['/b2b/contracts/new']);
  }

  exportExcel(): void {
    const size = Math.max(this.totalItems || 0, B2B_EXPORT_PAGE_SIZE);
    this.contractsService.listB2bContracts(1, size).subscribe({
      next: (response) => {
        const rows = (response.data || []).map(contract => ({
          ...contract,
          period: `${this.formatDate(contract.startDate)} até ${this.formatDate(contract.endDate)}`,
          statusLabel: this.getStatusLabel(contract.status)
        }));
        exportB2bTableToExcel('b2b-contratos', this.columns, rows, 'Contratos');
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  view(contract: B2bContract): void {
    this.router.navigate(['/b2b/contracts', contract.id, 'view']);
  }

  edit(contract: B2bContract): void {
    this.router.navigate(['/b2b/contracts', contract.id, 'edit']);
  }

  cancel(contract: B2bContract): void {
    this.contractToCancel = contract;
    this.cancelReasonCode = '';
    this.showCancelModal = true;
  }

  closeCancelModal(): void {
    this.showCancelModal = false;
    this.contractToCancel = null;
    this.cancelReasonCode = '';
  }

  getContractNumberToCancel(): string {
    if (!this.contractToCancel) return '';
    return this.contractToCancel.contractNumber || String(this.contractToCancel.id);
  }

  confirmCancelContract(): void {
    if (!this.contractToCancel) return;
    const contract = this.contractToCancel;
    const motivo = contractCancelMotivoFromCode(this.cancelReasonCode);
    if (!motivo) {
      this.errorService.showErrorMessage('Selecione o motivo do cancelamento.');
      return;
    }

    this.isLoading = true;
    this.contractsService.cancelB2bContract(contract.id, motivo).subscribe({
      next: (response) => {
        const baseMessage = response?.message || 'Contrato B2B cancelado com sucesso!';

        const finish = (msg: string) => {
          this.errorService.showInfoMessage(msg);
          this.loadContracts();
          this.closeCancelModal();
          this.isLoading = false;
        };

        const runAsaasCleanup = (cnpjSource: string) => {
          const digits = (cnpjSource || '').replace(/\D/g, '');
          if (!digits || (digits.length !== 11 && digits.length !== 14)) {
            finish(baseMessage);
            return;
          }

          this.paymentService.cancelPendingAsaasChargesAfterContractCancel(digits).subscribe({
            next: ({ hasOverdue, customerFound }) => {
              if (!customerFound) {
                finish(baseMessage + ' (cliente Asaas não encontrado).');
                return;
              }
              if (hasOverdue) {
                const idx = this.contracts.findIndex((c) => c.id === contract.id);
                if (idx !== -1) {
                  this.contracts[idx].status = 'Cancelled';
                  (this.contracts[idx] as B2bContract & { statusLabel?: string }).statusLabel =
                    'Cancelado / Pend. Fin.';
                }
                finish(baseMessage + ' Contrato cancelado com pendência financeira (boletos vencidos mantidos no Asaas).');
              } else {
                finish(baseMessage + ' Boletos pendentes cancelados no Asaas.');
              }
            },
            error: (err3) => {
              this.errorService.showErrorMessage(
                `Contrato cancelado, mas houve erro ao processar cobranças no Asaas: ${err3?.message || err3}`
              );
              this.loadContracts();
              this.closeCancelModal();
              this.isLoading = false;
            }
          });
        };

        // Mesmo fluxo do contrato padrão: CNPJ após cancelamento (detalhe do contrato)
        this.contractsService.getB2bContractById(contract.id).subscribe({
          next: (detail) => {
            const cnpj = (detail?.cnpj ?? contract.cnpj ?? '').toString();
            runAsaasCleanup(cnpj);
          },
          error: () => {
            runAsaasCleanup((contract.cnpj || '').toString());
          }
        });
      },
      error: (err) => {
        this.errorService.showErrorMessage(`Erro ao cancelar contrato B2B: ${err?.message || err}`);
        this.closeCancelModal();
        this.isLoading = false;
      }
    });
  }

  openFreezeModal(contract: B2bContract): void {
    if (!statusAllowsContractFreeze(contract.status)) {
      this.errorService.showErrorMessage(
        'Só é possível congelar contratos vigentes, próximos ao vencimento ou aguardando pagamento.'
      );
      return;
    }
    this.contractFreezeId = contract.id;
    this.contractFreezeSubtitle = contract.contractNumber || contract.contractName || `#${contract.id}`;
    this.showFreezeModal = true;
  }

  closeFreezeModal(): void {
    this.showFreezeModal = false;
    this.contractFreezeId = null;
    this.contractFreezeSubtitle = '';
  }

  confirmUnfreezeContract(contract: B2bContract): void {
    if (!statusAllowsContractUnfreeze(contract.status)) {
      this.errorService.showErrorMessage('Apenas contratos em estado congelado podem ser descongelados.');
      return;
    }
    this.confirmationService
      .confirm({
        title: 'Descongelar contrato',
        message:
          'Deseja descongelar este contrato agora (antes do fim do período de congelamento)?',
        confirmText: 'Descongelar',
        cancelText: 'Cancelar',
        type: 'warning'
      })
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.isLoading = true;
        this.contractsService.unfreezeContract(contract.id).subscribe({
          next: (res) => {
            this.errorService.showInfoMessage(res.message || 'Contrato descongelado com sucesso.');
            this.loadContracts();
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          }
        });
      });
  }

  onContractFrozen(res: ContractFreezeResponse): void {
    const msg =
      res?.message ||
      (res?.freezeStartUtc && res?.freezeEndUtc
        ? `Congelamento registrado (${res.status || 'Congelado'}).`
        : 'Contrato congelado com sucesso.');
    this.errorService.showInfoMessage(msg);
    this.closeFreezeModal();
    this.loadContracts();
  }

  download(contract: B2bContract): void {
    // TODO: Implementar download quando endpoint estiver disponível
    if (contract.contractDocumentUrl) {
      window.open(contract.contractDocumentUrl, '_blank');
    } else {
      this.errorService.showErrorMessage('Documento não disponível para download');
    }
  }

  onActionClick(actionData: { action: string; item: any }): void {
    // eslint-disable-next-line no-console
    const { action, item } = actionData;
    const contract = item as B2bContract;
    
    // eslint-disable-next-line no-console

    switch (action) {
      case 'view':
        this.view(contract);
        break;
      case 'edit':
        this.edit(contract);
        break;
      case 'drivers':
        this.viewDrivers(contract);
        break;
      case 'newDriver':
        this.newDriver(contract);
        break;
      case 'cancel':
        this.cancel(contract);
        break;
      case 'download':
        this.download(contract);
        break;
      case 'freeze':
        this.openFreezeModal(contract);
        break;
      case 'unfreeze':
        this.confirmUnfreezeContract(contract);
        break;
      default:
        // eslint-disable-next-line no-console
    }
  }

  viewDrivers(contract: B2bContract): void {
    this.router.navigate(['/b2b/contracts', contract.id, 'drivers']);
  }

  newDriver(contract: B2bContract): void {
    this.router.navigate(['/b2b/contracts', contract.id, 'drivers', 'new']);
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  }

  formatCurrency(value: number): string {
    if (!value && value !== 0) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  formatCnpj(value: string): string {
    if (!value) return '-';
    // Se já está formatado, retorna como está
    if (value.includes('/') || value.includes('-')) {
      return value;
    }
    const digits = String(value).replace(/\D/g, '');
    if (digits.length === 14) {
      // CNPJ
      return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    return value;
  }

  getStatusLabel(status: string): string {
    const statusMap: { [key: string]: string } = {
      'Draft': 'Rascunho',
      'Pending': 'Pendente',
      'Signed': 'Assinado',
      'Active': 'Ativo',
      'Expired': 'Expirado',
      'Cancelled': 'Cancelado'
    };
    return statusMap[status] || status || 'N/A';
  }
}

